// src/lib/analyticsUtils.js
import { supabase } from './supabaseClient';

/**
 * Calculate team form from recent fixtures
 * @param {string} teamName - Team name to calculate form for
 * @param {string} league - League name
 * @param {string} season - Season (e.g., "2025/26")
 * @param {number} numGames - Number of recent games to analyze (default: 5)
 * @returns {object} Form data with results array and summary
 */
export async function calculateTeamForm(teamName, league, season, numGames = 5) {
  try {
    // Fetch recent fixtures for this team (completed only)
    const { data: fixtures, error } = await supabase
      .from('fixtures')
      .select('*')
      .eq('league', league)
      .eq('season', season)
      .or(`home_team.eq.${teamName},away_team.eq.${teamName}`)
      .not('home_score', 'is', null)
      .not('away_score', 'is', null)
      .order('kickoff_time', { ascending: false })
      .limit(numGames);

    if (error) throw error;
    if (!fixtures || fixtures.length === 0) {
      return {
        results: [],
        summary: 'No recent matches available'
      };
    }

    // Calculate results (W/D/L)
    const results = fixtures.map(f => {
      const isHome = f.home_team === teamName;
      const teamScore = isHome ? f.home_score : f.away_score;
      const opponentScore = isHome ? f.away_score : f.home_score;

      if (teamScore > opponentScore) return 'W';
      if (teamScore < opponentScore) return 'L';
      return 'D';
    }).reverse(); // Reverse to show oldest to newest

    // Calculate summary
    const wins = results.filter(r => r === 'W').length;
    const draws = results.filter(r => r === 'D').length;
    const losses = results.filter(r => r === 'L').length;
    const points = (wins * 3) + draws;
    const maxPoints = numGames * 3;

    const summary = `${wins}W-${draws}D-${losses}L (${points} pts from ${maxPoints})`;

    return {
      results,
      summary,
      stats: {
        wins,
        draws,
        losses,
        points,
        played: results.length
      }
    };
  } catch (error) {
    return {
      results: [],
      summary: 'Form data unavailable'
    };
  }
}

/**
 * Fetch weather data for a fixture
 * @param {string} fixtureId - Fixture ID
 * @returns {object} Weather data
 */
export async function getFixtureWeather(fixtureId) {
  try {
    const { data, error } = await supabase
      .from('fixture_weather')
      .select('*')
      .eq('fixture_id', fixtureId)
      .maybeSingle();

    if (error || !data) {
      return null;
    }

    return {
      condition: data.weather_main,
      description: data.weather_description,
      temperature: data.temperature,
      feelsLike: data.feels_like,
      wind: `${Math.round(data.wind_speed * 2.237)} mph`, // Convert m/s to mph
      rain: data.rain_1h,
      clouds: data.clouds,
      impact: null
    };
  } catch (error) {
    return null;
  }
}

/**
 * Calculate head-to-head stats between two teams
 * @param {string} homeTeam - Home team name
 * @param {string} awayTeam - Away team name
 * @param {string} league - League name
 * @param {number} numGames - Number of recent H2H games (default: 5)
 * @returns {object} H2H stats
 */
export async function calculateHeadToHead(homeTeam, awayTeam, league, numGames = 5) {
  try {
    // Fetch recent meetings (completed only)
    const { data: fixtures, error } = await supabase
      .from('fixtures')
      .select('*')
      .eq('league', league)
      .or(`and(home_team.eq.${homeTeam},away_team.eq.${awayTeam}),and(home_team.eq.${awayTeam},away_team.eq.${homeTeam})`)
      .not('home_score', 'is', null)
      .not('away_score', 'is', null)
      .order('kickoff_time', { ascending: false })
      .limit(numGames);

    if (error) throw error;
    if (!fixtures || fixtures.length === 0) {
      return {
        summary: 'No previous meetings',
        lastMeetings: null,
        avgGoals: null
      };
    }

    // Calculate results from homeTeam perspective
    let homeWins = 0;
    let awayWins = 0;
    let draws = 0;
    let totalGoals = 0;

    fixtures.forEach(f => {
      totalGoals += (f.home_score + f.away_score);
      
      if (f.home_team === homeTeam) {
        if (f.home_score > f.away_score) homeWins++;
        else if (f.home_score < f.away_score) awayWins++;
        else draws++;
      } else {
        if (f.away_score > f.home_score) homeWins++;
        else if (f.away_score < f.home_score) awayWins++;
        else draws++;
      }
    });

    const avgGoals = (totalGoals / fixtures.length).toFixed(1);

    return {
      summary: `Last ${fixtures.length} meetings: ${homeWins} home wins, ${awayWins} away wins, ${draws} draws`,
      lastMeetings: `${homeWins}-${draws}-${awayWins}`,
      avgGoals,
      totalMeetings: fixtures.length
    };
  } catch (error) {
    return {
      summary: 'H2H data unavailable',
      lastMeetings: null,
      avgGoals: null
    };
  }
}

/**
 * Calculate venue stats for home team
 * @param {string} homeTeam - Home team name
 * @param {string} league - League name
 * @param {string} season - Season
 * @returns {object} Venue stats
 */
export async function calculateVenueStats(homeTeam, league, season) {
  try {
    // Fetch home fixtures this season (completed only)
    const { data: fixtures, error } = await supabase
      .from('fixtures')
      .select('*')
      .eq('home_team', homeTeam)
      .eq('league', league)
      .eq('season', season)
      .not('home_score', 'is', null)
      .not('away_score', 'is', null)
      .order('kickoff_time', { ascending: false });

    if (error) throw error;
    if (!fixtures || fixtures.length === 0) {
      return {
        homeRecord: 'No home games played yet',
        goalsScored: 0,
        goalsConceded: 0,
        recentForm: null
      };
    }

    // Calculate record
    let wins = 0;
    let draws = 0;
    let losses = 0;
    let goalsScored = 0;
    let goalsConceded = 0;

    fixtures.forEach(f => {
      goalsScored += f.home_score;
      goalsConceded += f.away_score;

      if (f.home_score > f.away_score) wins++;
      else if (f.home_score < f.away_score) losses++;
      else draws++;
    });

    return {
      homeRecord: `${wins}W-${draws}D-${losses}L`,
      goalsScored,
      goalsConceded,
      recentForm: `Last ${Math.min(fixtures.length, 5)} home games`,
      totalGames: fixtures.length
    };
  } catch (error) {
    return {
      homeRecord: 'Venue data unavailable',
      goalsScored: 0,
      goalsConceded: 0,
      recentForm: null
    };
  }
}

/**
 * Fetch all analytics for a fixture
 * Uses pre-computed cache for instant display (95% query reduction)
 * Falls back to real-time computation if cache miss
 * @param {object} fixture - Fixture object
 * @returns {object} Complete analytics data
 */
export async function getFixtureAnalytics(fixture) {
  if (!fixture) return null;

  const { home_team, away_team, league, season, venue, id } = fixture;

  try {
    // Try cache first (single query)
    const { data: cached, error: cacheError } = await supabase
      .from('fixture_analytics_cache')
      .select('*')
      .eq('fixture_id', id)
      .single();

    if (cached && !cacheError) {
      // Cache hit - instant return with weather
      const { data: weather } = await supabase
        .from('fixture_weather')
        .select('*')
        .eq('fixture_id', id)
        .maybeSingle();

      // Transform weather data to match expected format
      const weatherData = weather ? {
        condition: weather.weather_main,
        description: weather.weather_description,
        temperature: weather.temperature_celsius,
        feelsLike: weather.feels_like_celsius,
        wind: weather.wind_speed_ms ? `${Math.round(weather.wind_speed_ms * 2.237)} mph` : null,
        rain: weather.rain_1h_mm,
        clouds: weather.cloud_cover_percent
      } : null;

      return {
        homeForm: cached.home_form,
        awayForm: cached.away_form,
        h2h: cached.h2h,
        venueStats: cached.venue_stats,
        awayTeamStats: cached.away_team_stats,
        weather: weatherData,
        cached: true,
        computedAt: cached.computed_at
      };
    }

    // Cache miss - fallback to real-time (shouldn't happen after backfill)
    // Fetch all analytics in parallel (legacy fallback)
    const [homeForm, awayForm, weather, h2h, venueStats] = await Promise.all([
      calculateTeamForm(home_team, league, season, 5),
      calculateTeamForm(away_team, league, season, 5),
      getFixtureWeather(id),
      calculateHeadToHead(home_team, away_team, league, 5),
      calculateVenueStats(home_team, league, season)
    ]);

    return {
      homeForm,
      awayForm,
      weather,
      h2h,
      venueStats,
      cached: false
    };
  } catch (error) {
    return null;
  }
}
